import { useState, useEffect, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function DevicePage() {
  const [searchParams] = useSearchParams();
  const [userCode, setUserCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [deviceDetails, setDeviceDetails] = useState<{
    clientName: string;
    scopes: string[];
  } | null>(null);

  const prefilledCode = searchParams.get('user_code');

  useEffect(() => {
    if (prefilledCode) {
      setUserCode(prefilledCode);
      verifyCode(prefilledCode);
    }
  }, [prefilledCode]);

  const verifyCode = async (code: string) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/oauth2/device_verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ user_code: code }),
      });

      if (!response.ok) {
        throw new Error('Invalid or expired code');
      }

      const data = await response.json();
      setDeviceDetails(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setDeviceDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!userCode.trim()) {
      setError('Please enter a code');
      return;
    }
    verifyCode(userCode.toUpperCase());
  };

  const handleApprove = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/oauth2/device_verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          user_code: userCode,
          approved: 'true',
        }),
      });

      if (!response.ok) {
        throw new Error('Approval failed');
      }

      setSuccess(true);
      setDeviceDetails(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approval failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeny = async () => {
    setLoading(true);

    try {
      await fetch('/oauth2/device_verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          user_code: userCode,
          approved: 'false',
        }),
      });

      setDeviceDetails(null);
      setUserCode('');
    } catch (err) {
      console.error('Deny failed:', err);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-logo">ZoAuth</div>
          <div className="auth-subtitle">Device Authorized</div>
        </div>

        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <svg
            style={{ width: '64px', height: '64px', color: 'var(--success-color)' }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>

          <h2 style={{ marginTop: '16px', marginBottom: '8px', color: 'var(--success-color)' }}>
            Success!
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Your device has been authorized. You can now return to your device.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setSuccess(false);
            setUserCode('');
          }}
        >
          Authorize Another Device
        </button>
      </div>
    );
  }

  if (deviceDetails) {
    return (
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-logo">ZoAuth</div>
          <div className="auth-subtitle">Authorize Device</div>
        </div>

        <div className="device-code">
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Code:</p>
          <div className="code-display">{userCode}</div>
        </div>

        <div className="client-info">
          <div className="client-name">{deviceDetails.clientName}</div>
          <div className="client-description">wants to access your account</div>
        </div>

        {deviceDetails.scopes.length > 0 && (
          <>
            <p style={{ marginBottom: '12px', fontSize: '14px', fontWeight: 500 }}>
              Permissions requested:
            </p>
            <ul className="scope-list">
              {deviceDetails.scopes.map((scope) => (
                <li key={scope} className="scope-item">
                  <span className="scope-icon">✓</span>
                  <div>{scope}</div>
                </li>
              ))}
            </ul>
          </>
        )}

        {error && (
          <div className="error-message" style={{ marginTop: '16px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleDeny}
            disabled={loading}
            style={{ flex: 1 }}
          >
            Deny
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleApprove}
            disabled={loading}
            style={{ flex: 1 }}
          >
            {loading ? (
              <>
                <div className="loading-spinner" />
                Authorizing...
              </>
            ) : (
              'Authorize'
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-header">
        <div className="auth-logo">ZoAuth</div>
        <div className="auth-subtitle">Device Authorization</div>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <svg
          style={{ width: '48px', height: '48px', color: 'var(--primary-color)' }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
        <p style={{ marginTop: '12px', color: 'var(--text-secondary)', fontSize: '14px' }}>
          Enter the code displayed on your device
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="userCode">
            Device Code
          </label>
          <input
            id="userCode"
            type="text"
            className={`form-input ${error ? 'error' : ''}`}
            placeholder="ABCD-EFGH"
            value={userCode}
            onChange={(e) => setUserCode(e.target.value.toUpperCase())}
            style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '2px' }}
            required
            autoFocus
          />
          {error && <div className="error-message">{error}</div>}
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? (
            <>
              <div className="loading-spinner" />
              Verifying...
            </>
          ) : (
            'Continue'
          )}
        </button>
      </form>

      <div className="footer-links">
        <a href="/login" className="footer-link">
          Back to Sign In
        </a>
      </div>
    </div>
  );
}
